import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  clearAuthStorage,
  loadCachedUser,
  loadSession,
  saveCachedUser,
  saveSession,
  type CachedUser,
  type Session,
} from "./storage";
import {
  fetchMe,
  logoutSession,
  toSession,
  type User,
} from "./api";
import { refreshSingleFlight } from "./refresh";

// Spec §4.2: refresh when "less than 5 minutes remaining".
const REFRESH_LEAD_MS = 5 * 60 * 1000;
const TICK_MS = 60 * 1000;

export type AuthState =
  | { status: "loading" }
  | { status: "unauthenticated" }
  // Have working tokens AND have validated the user with the server at some
  // point this session.
  | { status: "authenticated"; session: Session; user: User; online: boolean }
  // Have cached tokens, server contact has not yet succeeded this session.
  // The SPA stays usable in this state — spec §4.2 "continues to function in
  // full read/write mode against the local cache."  Promotes to `authenticated`
  // as soon as a network call works.
  | { status: "offline"; session: Session; user: CachedUser | null }
  // Server has explicitly rejected our refresh token.  Spec §4.2: "non-blocking
  // banner asking the user to re-authenticate, but continues to allow local
  // read/write until they do."  We deliberately keep the cached user here so
  // the banner can address them by name.
  | { status: "reauth_required"; user: CachedUser | null };

type Ctx = {
  state: AuthState;
  /** Adopt a token bundle the SPA just received (e.g. from /auth/complete). */
  setSession: (s: Session) => Promise<void>;
  /** Patch the current user in state + IDB cache (after PATCH /me). */
  setUser: (u: User) => Promise<void>;
  signOut: () => Promise<void>;
};

const AuthCtx = createContext<Ctx | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: "loading" });

  // Ref mirror so the periodic refresher always sees the latest session
  // without re-subscribing on every state change.
  const sessionRef = useRef<Session | null>(null);

  const promoteWithUser = useCallback(
    async (session: Session, user: User) => {
      sessionRef.current = session;
      await saveCachedUser({
        id: user.id,
        email: user.email,
        display_name: user.display_name,
        handle: user.handle,
        is_admin: user.is_admin,
      });
      setState({ status: "authenticated", session, user, online: true });
    },
    [],
  );

  const setReauthRequired = useCallback(async () => {
    const cached = await loadCachedUser();
    await clearAuthStorage();
    sessionRef.current = null;
    setState({ status: "reauth_required", user: cached });
  }, []);

  // Validate a session against the server.  Promotes to `authenticated` on
  // success; falls through to refresh on 401; preserves state on network error.
  const validate = useCallback(
    async (session: Session) => {
      const me = await fetchMe(session.access_token);
      if (me.ok) {
        await promoteWithUser(session, me.value);
        return;
      }
      if (me.kind === "network" || me.kind === "server") {
        // Couldn't reach the API — keep the session and run as offline.  The
        // background refresher will retry.
        const cached = await loadCachedUser();
        sessionRef.current = session;
        setState({ status: "offline", session, user: cached });
        return;
      }
      // auth failure → try refresh once.
      const refreshed = await refreshSingleFlight(session.refresh_token);
      if (!refreshed.ok) {
        if (refreshed.kind === "auth") {
          await setReauthRequired();
        } else {
          // Network/server failure during refresh — stay offline.
          const cached = await loadCachedUser();
          sessionRef.current = session;
          setState({ status: "offline", session, user: cached });
        }
        return;
      }
      const next = toSession(refreshed.value);
      await saveSession(next);
      const meAgain = await fetchMe(next.access_token);
      if (meAgain.ok) {
        await promoteWithUser(next, meAgain.value);
      } else if (meAgain.kind === "auth") {
        await setReauthRequired();
      } else {
        const cached = await loadCachedUser();
        sessionRef.current = next;
        setState({ status: "offline", session: next, user: cached });
      }
    },
    [promoteWithUser, setReauthRequired],
  );

  // Boot: rehydrate from IndexedDB.
  useEffect(() => {
    void (async () => {
      const session = await loadSession();
      if (!session) {
        setState({ status: "unauthenticated" });
        return;
      }
      await validate(session);
    })();
  }, [validate]);

  // Background refresher + offline retry. Fires every TICK_MS and also runs
  // once immediately so a near-expired token doesn't sit unrefreshed for up
  // to a full minute after a tab regains focus.
  useEffect(() => {
    let alive = true;

    const tick = async () => {
      const current = sessionRef.current;
      if (!current) return;

      const needsRefresh =
        current.expires_at - Date.now() <= REFRESH_LEAD_MS;
      if (!needsRefresh) {
        // Take the opportunity to recover from offline if we have a still-valid
        // access token but couldn't reach the API earlier.
        if (state.status === "offline") {
          const me = await fetchMe(current.access_token);
          if (alive && me.ok) await promoteWithUser(current, me.value);
        }
        return;
      }
      const refreshed = await refreshSingleFlight(current.refresh_token);
      if (!alive) return;
      if (!refreshed.ok) {
        if (refreshed.kind === "auth") await setReauthRequired();
        // network/server: leave state alone, try again next tick
        return;
      }
      const next = toSession(refreshed.value);
      await saveSession(next);
      sessionRef.current = next;
      // Once we have fresh tokens, refresh user data if we don't yet have it
      // or if we were offline.
      const me = await fetchMe(next.access_token);
      if (!alive) return;
      if (me.ok) {
        await promoteWithUser(next, me.value);
      } else if (me.kind === "auth") {
        await setReauthRequired();
      } else {
        const cached = await loadCachedUser();
        setState({ status: "offline", session: next, user: cached });
      }
    };

    const id = window.setInterval(() => void tick(), TICK_MS);
    void tick();
    return () => {
      alive = false;
      window.clearInterval(id);
    };
    // We intentionally exclude `state` from deps — sessionRef holds the live
    // token and we use functional setState updates. Re-running the effect on
    // every state change would tear down the interval needlessly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [promoteWithUser, setReauthRequired]);

  const value = useMemo<Ctx>(
    () => ({
      state,
      async setSession(s) {
        await saveSession(s);
        await validate(s);
      },
      async setUser(u) {
        await saveCachedUser({
          id: u.id,
          email: u.email,
          display_name: u.display_name,
          handle: u.handle,
          is_admin: u.is_admin,
        });
        // Preserve transport state (authenticated vs offline); only swap user.
        setState((prev) => {
          if (prev.status === "authenticated") {
            return { ...prev, user: u };
          }
          if (prev.status === "offline") {
            return { ...prev, user: u };
          }
          return prev;
        });
      },
      async signOut() {
        const current = sessionRef.current;
        if (current) await logoutSession(current.refresh_token);
        await clearAuthStorage();
        sessionRef.current = null;
        setState({ status: "unauthenticated" });
      },
    }),
    [state, validate],
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth(): Ctx {
  const v = useContext(AuthCtx);
  if (!v) throw new Error("useAuth must be used inside <AuthProvider>");
  return v;
}
